// CSV import for the Delivery view + landing-page entry.
//
// Three render states drive the user through a small wizard:
//   1. pick      — drag a file or browse
//   2. wizard    — show preview; if no `migration_unit` column, ask the
//                  user how to assign MUs (from-column / ai-group /
//                  single-mu / unassigned). When the column IS present,
//                  we skip straight to confirm.
//   3. duplicates — server returned existing-id collisions; user picks
//                   keep-existing vs overwrite.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Upload, AlertCircle, CheckCircle2, Sparkles, Layers, Inbox } from 'lucide-react';
import { parseCsv, type CsvParseRow, type CsvParseResult } from '../utils/csvFeatureImport';
import type { ImportCsvResult, MuMode } from '../hooks/sessionTypes';
import '../styles/DeliveryCsvImportModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onImport: (
    rows: CsvParseRow[],
    opts: { muMode: MuMode; force?: boolean; singleMuName?: string },
  ) => Promise<ImportCsvResult | null>;
  importing: boolean;
  // Optional starting MU mode for use cases that already know how to bucket
  // (e.g. landing-page CSV start often wants AI-group as the default).
  defaultMuMode?: MuMode;
  title?: string;
}

const DEFAULT_SINGLE_MU_NAME = 'Imported features';

function scopeTypeShort(t: 'bc' | 'aggregate' | 'feature'): string {
  if (t === 'bc') return 'BC';
  if (t === 'aggregate') return 'Agg';
  return 'Feat';
}

const DeliveryCsvImportModal: React.FC<Props> = ({
  isOpen, onClose, onImport, importing, defaultMuMode, title,
}) => {
  const [parsed, setParsed] = useState<CsvParseResult | null>(null);
  const [muMode, setMuMode] = useState<MuMode>(defaultMuMode || 'ai-group');
  const [singleMuName, setSingleMuName] = useState(DEFAULT_SINGLE_MU_NAME);
  const [duplicates, setDuplicates] = useState<Array<{ candidateId: string; name: string }>>([]);
  const [pendingRows, setPendingRows] = useState<CsvParseRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const csvHasMuColumn = useMemo(
    () => !!parsed && parsed.rows.some((r) => r.migrationUnit),
    [parsed],
  );

  // When the column IS present, default the mode to from-column and lock it.
  useEffect(() => {
    if (csvHasMuColumn) setMuMode('from-column');
  }, [csvHasMuColumn]);

  const reset = useCallback(() => {
    setParsed(null);
    setMuMode(defaultMuMode || 'ai-group');
    setSingleMuName(DEFAULT_SINGLE_MU_NAME);
    setDuplicates([]);
    setPendingRows(null);
    setError(null);
    setDragOver(false);
  }, [defaultMuMode]);

  const handleFile = useCallback(async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const result = parseCsv(text);
      setParsed(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to read file');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const closeAndReset = useCallback(() => { reset(); onClose(); }, [reset, onClose]);

  const handleConfirm = useCallback(async () => {
    if (!parsed || parsed.rows.length === 0) return;
    setPendingRows(parsed.rows);
    const result = await onImport(parsed.rows, {
      muMode,
      singleMuName: muMode === 'single-mu' ? singleMuName : undefined,
    });
    if (!result) {
      setError('Import failed — see console for details.');
      setPendingRows(null);
      return;
    }
    if (result.duplicates.length > 0) {
      setDuplicates(result.duplicates);
    } else {
      closeAndReset();
    }
  }, [parsed, muMode, singleMuName, onImport, closeAndReset]);

  const handleOverwriteDupes = useCallback(async () => {
    if (!pendingRows) return;
    const result = await onImport(pendingRows, {
      muMode,
      force: true,
      singleMuName: muMode === 'single-mu' ? singleMuName : undefined,
    });
    if (!result) {
      setError('Overwrite failed.');
      return;
    }
    closeAndReset();
  }, [pendingRows, muMode, singleMuName, onImport, closeAndReset]);

  const handleSkipDupes = useCallback(() => closeAndReset(), [closeAndReset]);

  if (!isOpen) return null;

  return (
    <div className="dv-modal-backdrop" onClick={closeAndReset}>
      <div className="dv-modal" onClick={(e) => e.stopPropagation()}>
        <header className="dv-modal-head">
          <h3>{title || 'Import features from CSV'}</h3>
          <button type="button" className="dv-modal-close" onClick={closeAndReset}>
            <X size={14} />
          </button>
        </header>

        <div className="dv-modal-body">
          {error && (
            <div className="dv-modal-error" role="alert">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {duplicates.length > 0 ? (
            <DuplicatesView
              duplicates={duplicates}
              onOverwrite={handleOverwriteDupes}
              onSkip={handleSkipDupes}
              importing={importing}
            />
          ) : !parsed ? (
            <DropZone dragOver={dragOver} onDragChange={setDragOver} onDrop={handleDrop} onPick={handleFile} />
          ) : (
            <PreviewAndWizard
              parsed={parsed}
              csvHasMuColumn={csvHasMuColumn}
              muMode={muMode}
              setMuMode={setMuMode}
              singleMuName={singleMuName}
              setSingleMuName={setSingleMuName}
              importing={importing}
              onCancel={reset}
              onConfirm={handleConfirm}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryCsvImportModal;

// ── Sub-views ─────────────────────────────────────────────────────

interface DropZoneProps {
  dragOver: boolean;
  onDragChange: (over: boolean) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onPick: (file: File) => void;
}

const DropZone: React.FC<DropZoneProps> = ({ dragOver, onDragChange, onDrop, onPick }) => (
  <div
    className={`dv-dropzone ${dragOver ? 'dv-dropzone--active' : ''}`}
    onDragOver={(e) => { e.preventDefault(); onDragChange(true); }}
    onDragLeave={(e) => { if (e.currentTarget === e.target) onDragChange(false); }}
    onDrop={onDrop}
  >
    <Upload size={28} />
    <p className="dv-dropzone-title">Drop a CSV file here, or pick one</p>
    <p className="dv-dropzone-sub">
      Required: <code>name</code>. Optional: <code>description</code>, <code>bounded_context</code>,
      <code>priority</code>, <code>migration_unit</code>, <code>effort</code>, <code>value</code>, <code>tags</code>.
    </p>
    <label className="dv-btn dv-btn--primary">
      Choose file
      <input
        type="file"
        accept=".csv,.tsv,text/csv,text/tab-separated-values"
        style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files?.[0]) onPick(e.target.files[0]); }}
      />
    </label>
  </div>
);

interface PreviewProps {
  parsed: CsvParseResult;
  csvHasMuColumn: boolean;
  muMode: MuMode;
  setMuMode: (m: MuMode) => void;
  singleMuName: string;
  setSingleMuName: (n: string) => void;
  importing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const PreviewAndWizard: React.FC<PreviewProps> = ({
  parsed, csvHasMuColumn, muMode, setMuMode, singleMuName, setSingleMuName,
  importing, onCancel, onConfirm,
}) => {
  const hasRows = parsed.rows.length > 0;
  const top = parsed.rows.slice(0, 5);
  const moreCount = parsed.rows.length - top.length;
  const distinctMus = useMemo(() => {
    const set = new Set<string>();
    for (const r of parsed.rows) if (r.migrationUnit) set.add(r.migrationUnit);
    return [...set];
  }, [parsed.rows]);

  return (
    <>
      <div className="dv-preview-summary">
        <CheckCircle2 size={14} />
        <span>
          <strong>{parsed.rows.length}</strong> row{parsed.rows.length === 1 ? '' : 's'} ready
          {parsed.errors.length > 0 && <> · <strong>{parsed.errors.length}</strong> rejected</>}
          {parsed.warnings.length > 0 && <> · <strong>{parsed.warnings.length}</strong> warning{parsed.warnings.length === 1 ? '' : 's'}</>}
        </span>
      </div>

      {parsed.errors.length > 0 && (
        <details className="dv-preview-errors">
          <summary>{parsed.errors.length} rejected row{parsed.errors.length === 1 ? '' : 's'}</summary>
          <ul>
            {parsed.errors.slice(0, 10).map((e, i) => (
              <li key={i}>Row {e.rowIndex}: {e.reason}</li>
            ))}
            {parsed.errors.length > 10 && <li>…+{parsed.errors.length - 10} more</li>}
          </ul>
        </details>
      )}
      {parsed.warnings.length > 0 && (
        <details className="dv-preview-warnings">
          <summary>{parsed.warnings.length} warning{parsed.warnings.length === 1 ? '' : 's'}</summary>
          <ul>
            {parsed.warnings.slice(0, 10).map((w, i) => <li key={i}>{w}</li>)}
            {parsed.warnings.length > 10 && <li>…+{parsed.warnings.length - 10} more</li>}
          </ul>
        </details>
      )}

      {hasRows && (
        <table className="dv-preview-table">
          <thead>
            <tr>
              <th>GUID</th>
              <th>Type</th>
              <th>Name</th>
              <th>BC</th>
              <th>MU</th>
              <th>Effort</th>
              <th>Conf.</th>
            </tr>
          </thead>
          <tbody>
            {top.map((r, i) => (
              <tr key={i}>
                <td className="dv-preview-guid">{r.guid || '—'}</td>
                <td className="dv-preview-scope">{r.scopeType ? scopeTypeShort(r.scopeType) : '—'}</td>
                <td>{r.name}</td>
                <td>{r.boundedContext || '—'}</td>
                <td>{r.migrationUnit || '—'}</td>
                <td>{r.effort ?? '—'}</td>
                <td>{r.specConfidence || r.value || '—'}</td>
              </tr>
            ))}
            {moreCount > 0 && (
              <tr>
                <td colSpan={7} className="dv-preview-more">+{moreCount} more row{moreCount === 1 ? '' : 's'}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Wizard: only when CSV has no MU column */}
      {hasRows && !csvHasMuColumn && (
        <div className="dv-wizard">
          <p className="dv-wizard-question">How should we organise these {parsed.rows.length} features into Migration Units?</p>
          <ul className="dv-wizard-options">
            <WizardOption
              icon={<Sparkles size={14} />}
              label="AI-group them for me"
              hint="Recommended — the LLM proposes MUs based on feature shape."
              selected={muMode === 'ai-group'}
              onSelect={() => setMuMode('ai-group')}
            />
            <WizardOption
              icon={<Layers size={14} />}
              label="Put them all in one MU"
              hint="A single bucket — split manually after import."
              selected={muMode === 'single-mu'}
              onSelect={() => setMuMode('single-mu')}
            >
              {muMode === 'single-mu' && (
                <input
                  className="dv-wizard-input"
                  value={singleMuName}
                  onChange={(e) => setSingleMuName(e.target.value)}
                  maxLength={30}
                  placeholder="MU name"
                />
              )}
            </WizardOption>
            <WizardOption
              icon={<Inbox size={14} />}
              label="Leave them unassigned"
              hint="Drop into the Unassigned bucket — you'll create MUs manually."
              selected={muMode === 'unassigned'}
              onSelect={() => setMuMode('unassigned')}
            />
          </ul>
        </div>
      )}

      {hasRows && csvHasMuColumn && (
        <div className="dv-wizard">
          <p className="dv-wizard-note">
            CSV has a <code>migration_unit</code> column — features will be grouped using those values
            ({distinctMus.length} distinct MU{distinctMus.length === 1 ? '' : 's'}).
          </p>
        </div>
      )}

      <footer className="dv-modal-foot">
        <button type="button" className="dv-btn dv-btn--ghost" onClick={onCancel} disabled={importing}>
          Pick different file
        </button>
        <button
          type="button"
          className="dv-btn dv-btn--primary"
          onClick={onConfirm}
          disabled={!hasRows || importing || (muMode === 'single-mu' && !singleMuName.trim())}
        >
          {importing ? 'Importing…' : `Import ${parsed.rows.length} feature${parsed.rows.length === 1 ? '' : 's'}`}
        </button>
      </footer>
    </>
  );
};

interface WizardOptionProps {
  icon: React.ReactNode;
  label: string;
  hint: string;
  selected: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}

const WizardOption: React.FC<WizardOptionProps> = ({ icon, label, hint, selected, onSelect, children }) => (
  <li>
    <button
      type="button"
      className={`dv-wizard-opt ${selected ? 'dv-wizard-opt--selected' : ''}`}
      onClick={onSelect}
    >
      <span className="dv-wizard-radio">{selected ? '●' : '○'}</span>
      <span className="dv-wizard-icon">{icon}</span>
      <span className="dv-wizard-text">
        <span className="dv-wizard-label">{label}</span>
        <span className="dv-wizard-hint">{hint}</span>
      </span>
    </button>
    {children}
  </li>
);

interface DupesProps {
  duplicates: Array<{ candidateId: string; name: string }>;
  onOverwrite: () => void;
  onSkip: () => void;
  importing: boolean;
}

const DuplicatesView: React.FC<DupesProps> = ({ duplicates, onOverwrite, onSkip, importing }) => (
  <>
    <div className="dv-preview-summary">
      <AlertCircle size={14} />
      <span>
        <strong>{duplicates.length}</strong> row{duplicates.length === 1 ? '' : 's'} already exist in this session.
      </span>
    </div>
    <ul className="dv-dupes-list">
      {duplicates.slice(0, 20).map((d) => <li key={d.candidateId}>{d.name}</li>)}
      {duplicates.length > 20 && <li>…+{duplicates.length - 20} more</li>}
    </ul>
    <footer className="dv-modal-foot">
      <button type="button" className="dv-btn dv-btn--ghost" onClick={onSkip} disabled={importing}>
        Keep existing (skip dupes)
      </button>
      <button type="button" className="dv-btn dv-btn--primary" onClick={onOverwrite} disabled={importing}>
        Overwrite duplicates
      </button>
    </footer>
  </>
);
